import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { LuAlertCircle, LuHash } from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditCategory,
  getIsCategoryModalOpen,
  toggleCategoryModal,
} from "../../features/categories/categoriesSlice";
import { ICategoryFormInput } from "../../types/category";
import { FileTextIcon, TagIcon } from "../icons";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formFieldIconClass,
} from "../ui/styles";

type Props = {
  handleSaveCategory: (data: ICategoryFormInput) => Promise<boolean>;
};

const CategoryFormModal = ({ handleSaveCategory }: Props) => {
  const dispatch = useAppDispatch();
  const editCategory = useAppSelector(getEditCategory);
  const isCategoryModalOpen = useAppSelector(getIsCategoryModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<ICategoryFormInput>({
    defaultValues: { name: "", code: "", description: "" },
  });

  const handleOnSubmit: SubmitHandler<ICategoryFormInput> = async (data) => {
    setIsLoading(true);
    const saved = await handleSaveCategory(data);
    setIsLoading(false);

    if (saved) {
      handleCloseFormModal();
    }
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleCategoryModal(false));
  };

  useEffect(() => {
    if (editCategory && editCategory.id) {
      reset({
        name: editCategory.name,
        code: editCategory.code,
        description: editCategory.description || "",
      });
    } else {
      reset({ name: "", code: "", description: "" });
    }
  }, [editCategory, isCategoryModalOpen, reset]);

  return (
    <Dialog open={isCategoryModalOpen === true} onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>કેટેગરી ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.name && "error")}>
                <TagIcon className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="કેટેગરીનું નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.name)}
                  {...register("name", { required: true })}
                />
                {errors.name && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  કેટેગરીનું નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                કોડ (પ્રીફિક્સ) <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.code && "error")}>
                <LuHash className={formFieldIconClass} />
                <Input
                  type="text"
                  maxLength={10}
                  placeholder="કોડ દાખલ કરો (દા.ત. AB, AB-CD)"
                  className={cx(fieldInputClass(!!errors.code), "uppercase")}
                  {...register("code", {
                    required: true,
                    // Same rule as the API: hyphens only between letters/digits.
                    pattern: /^[A-Z0-9]+(-[A-Z0-9]+)*$/,
                    setValueAs: (value) =>
                      String(value ?? "").trim().toUpperCase(),
                  })}
                />
                {errors.code && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {errors.code && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.code.type === "pattern"
                    ? "કોડમાં ફક્ત A-Z, 0-9 અને - વાપરો (- શરૂઆતમાં, છેલ્લે કે સળંગ બે વાર નહીં)"
                    : "કોડ દાખલ કરો"}
                </p>
              )}
              <p className="text-xs text-gray-500">
                કોડ કેપિટલ અક્ષરમાં સેવ થશે
              </p>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>ડિસ્ક્રિપ્શન (વૈકલ્પિક)</label>
              <div className="relative">
                <FileTextIcon className={formFieldIconClass} />
                <Textarea
                  placeholder="કેટેગરીનું ડિસ્ક્રિપ્શન દાખલ કરો"
                  className={fieldTextareaClass()}
                  {...register("description", { required: false })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryFormModal;
